import { AgoraPlanNameE, SupportedCountriesE } from '@/types/cyclone/models';

// Ágora UP es un plan tier-professional (agenda + servicios + caja + herramientas
// financieras + productos incluidos). Toda rama de UI gateada "solo professional" debe pasar por
// acá para que los planes hermanos no queden con la app vacía (bug del side menu
// con agora_up, jul 2026).
export const isProfessionalLikePlan = (planName?: string | null): boolean =>
  planName === AgoraPlanNameE.PROFESSIONAL ||
  planName === AgoraPlanNameE.AGORA_UP ||
  planName === AgoraPlanNameE.CAR;

/**
 * Comisiones necesita equipo. Entran el Plan Negocio legacy (una cuenta
 * `professional` que además es dueña de un venue) y Ágora UP, que vende hasta
 * cuatro profesionales en la base. `operator` (Plan Negocio) también puede tener
 * estructura de equipo, pero su habilitación sigue siendo comercial.
 */
export const isRevenueShareVenuePlan = (planName?: string | null, isVenue?: boolean): boolean =>
  Boolean(isVenue) &&
  (planName === AgoraPlanNameE.PROFESSIONAL ||
    planName === AgoraPlanNameE.AGORA_UP ||
    planName === AgoraPlanNameE.CAR);

/**
 * Ágora UP: profesionales INCLUIDOS en la base sin cargo — recién del 5to en adelante suma
 * `agora_up_staff_price`. Espejo de `AGORA_UP_INCLUDED_STAFF` en cyclone (`src/constants/plan.ts`):
 * si cambia allá, cambiar acá.
 */
export const AGORA_UP_INCLUDED_STAFF = 4;

/** Plan Car: 10 agendas incluidas (con la del dueño); cada una más, $10.000/mes. Espejo de cyclone. */
export const CAR_INCLUDED_STAFF = 10;
export const CAR_STAFF_PRICE = 10000;

/**
 * Ágora UP incluye las herramientas financieras y el catálogo de productos en su precio base —
 * cyclone conserva sus flags `enabled` y los cotiza en $0 (`consumption-calculator.ts`). Comisiones
 * también entra, vía `isRevenueShareVenuePlan`, cuando además es dueño de venue. En "Mi plan" estos
 * módulos no van como add-ons contratables porque no se pagan aparte ni se pueden resignar.
 */
export const planIncludesTeamAddOns = (planName?: string | null): boolean =>
  planName === AgoraPlanNameE.AGORA_UP || planName === AgoraPlanNameE.CAR;

/** Operator y Ágora UP incluyen Finanzas en la base y no suman cargo al activarlo. */
export const planIncludesFinancialTools = (planName?: string | null): boolean =>
  planName === AgoraPlanNameE.OPERATOR ||
  planName === AgoraPlanNameE.AGORA_UP ||
  planName === AgoraPlanNameE.CAR;

/**
 * Nombre comercial del plan ("Mi plan" y el pie del menú lateral). `operator` y el
 * legacy `professional` que además es dueño de venue se venden los dos como Plan
 * Negocio: "Venue" es nombre interno y nunca se le muestra al negocio.
 */
export const getPlanDisplayName = (planName?: string | null, isVenue?: boolean): string => {
  if (planName === AgoraPlanNameE.CAR) return 'Plan Car';
  if (planName === AgoraPlanNameE.AGORA_UP) return 'Ágora UP';
  if (planName === AgoraPlanNameE.OPERATOR) return 'Plan Negocio';
  if (isVenue && planName === AgoraPlanNameE.PROFESSIONAL) return 'Plan Negocio';
  if (planName === AgoraPlanNameE.EVENT) return 'Plan Eventos';
  if (planName === AgoraPlanNameE.ACADEMY) return 'Plan Academia';
  return 'Plan Profesional';
};

export const getPricesByCountry = (country: SupportedCountriesE) => {
  switch (country) {
    case SupportedCountriesE.CHILE:
      return {
        professional_base_price: 9900,
        venue_base_price: 19900,
        venue_staff_price: 3000,
        agora_up_base_price: 54900,
        agora_up_staff_price: 4000,
        referral_discount: 2000,
        whatsapp_reminders_per_50_messages: 4000,
        revenue_share: 10000,
        catalog_products: 5000,
        community: 5000
      };
    case SupportedCountriesE.BOLIVIA:
      return {
        professional_base_price: 0,
        venue_base_price: 0,
        venue_staff_price: 0,
        agora_up_base_price: 649,
        agora_up_staff_price: 50,
        referral_discount: 0,
        whatsapp_reminders_per_50_messages: 50,
        revenue_share: 0,
        catalog_products: 0,
        community: 0
      };
    case SupportedCountriesE.URUGUAY:
    case SupportedCountriesE.MEXICO:
      return {
        professional_base_price: 599,
        venue_base_price: 899,
        venue_staff_price: 200,
        // UY $2.499 · MX no ofrece UP, pero comparte esta rama de tarifas
        agora_up_base_price: 2499,
        agora_up_staff_price: 200,
        referral_discount: 100,
        whatsapp_reminders_per_50_messages: 200,
        revenue_share: 400,
        catalog_products: 200,
        community: 200
      };
    default:
      return {
        professional_base_price: 11900,
        venue_base_price: 18900,
        venue_staff_price: 5000,
        agora_up_base_price: 69900,
        agora_up_staff_price: 5000,
        referral_discount: 2000,
        whatsapp_reminders_per_50_messages: 5000,
        revenue_share: 10000,
        catalog_products: 5000,
        community: 5000
      };
  }
};
